import React from 'react';
import styled from 'styled-components';
import BREAK_POINTS from 'packages/smart-home-support/styling/media-queries';
import getPartnerAsset, { DynamicAssetsKeys } from 'packages/smart-home-support/assets';
import { DynamicLink } from 'packages/smart-home-support/components';
import { useUser } from 'packages/smart-home-support/hooks';
import { CallToAction } from 'packages/smart-home-support/styling/styles';
import { NavItem } from 'packages/smart-home-support/assets/models';
import { phoneToUrl, prepareNavItems } from 'packages/smart-home-support/utils';
import { partnerValue, usePartner } from 'packages/smart-home-support/partner';
import { FooterProps } from '../models';
import Copyright from '../Copyright';
import { legal as legalNavItems } from '../data/navItems';
import asurionLogo from '../../resources/asurion-logo-white.svg';

const Background = styled.div`
  height: inherit;
  background: ${({ theme }) =>
    `linear-gradient(180deg, ${theme.colors.gradient2Light} -24.61%, ${theme.colors.gradient2Dark} 100%)`};
`;

const Container = styled.div`
  padding: 50px 5px 125px 5px;

  @media ${BREAK_POINTS.tablet} {
    padding: 50px 50px 125px 50px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    margin-right: auto;
    margin-left: auto;
  }

  @media ${BREAK_POINTS.desktop} {
    max-width: 1440px;
  }
`;

const FileClaimText = styled.div`
  color: white;
  font-size: 18px;
  margin-bottom: 5px;
`;

const ClaimPhoneText = styled.div`
  display: none;

  @media ${BREAK_POINTS.tablet} {
    color: white;
    font-size: 22px;
    display: inherit;
  }
`;

enum Sides {
  Left,
  Right,
  Top,
  Bottom,
}

interface SectionProps {
  side: Sides;
}

const Section = styled.div<SectionProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${({ side }) => (side === Sides.Bottom ? '50px' : 'inherit')};

  @media ${BREAK_POINTS.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: ${({ side }) => (side === Sides.Top ? 'flex-start' : 'flex-end')};
  }
`;

const SectionGroup = styled.div<SectionProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  @media ${BREAK_POINTS.tablet} {
    align-items: ${({ side }) => (side === Sides.Left ? 'flex-start' : 'flex-end')};
  }
`;

const Link = styled(DynamicLink)`
  color: white;
  font-size: 16px;
  margin-bottom: 20px;
  text-decoration: none;
`;

const InformativeLink = styled(DynamicLink)`
  color: white;
  font-size: 12px;

  @media ${BREAK_POINTS.tablet} {
    font-size: 14px;
  }
`;

const CallButton = styled.a`
  ${CallToAction}
  border: 1px solid white;
  background-color: rgb(0, 0, 0, 0);
  color: white;
  margin-bottom: 50px;
  margin-top: 5px;
  padding: 16px 30px;

  :hover {
    background-color: rgb(0, 0, 0, 0);
    color: white;
  }

  :focus {
    background-color: rgb(0, 0, 0, 0);
    color: white;
  }

  @media ${BREAK_POINTS.tablet} {
    display: none;
  }
`;

const OnlineClaimButton = styled.a`
  ${CallToAction}
  border: 1px solid white;
  width: 210px;
  background-color: white;
  color: #1b1b1b;
  margin-bottom: 50px;
  margin-top: -25px;

  @media ${BREAK_POINTS.tablet} {
    margin-bottom: 25px;
    margin-top: 25px;
  }

  :hover {
    background-color: rgb(0, 0, 0, 0);
    color: white;
  }

  :focus {
    background-color: rgb(0, 0, 0, 0);
    color: white;
  }
`;

const LogosContainer = styled.div`
  display: flex;
`;

const LogoLink = styled.a`
  display: flex;
  align-items: center;
`;

const AsurionLogo = styled.img`
  height: 20px;
  margin-top: -3px;

  @media ${BREAK_POINTS.desktop} {
    height: 30px;
    margin-top: -2px;
  }
`;

const PartnerLogo = styled.img`
  height: 35px;
  align-self: center;

  @media ${BREAK_POINTS.desktop} {
    height: 55px;
  }
`;

const LogoSeparator = styled.div`
  margin-right: 20px;
  margin-left: 20px;
  border-right: 1px solid white;

  @media ${BREAK_POINTS.tablet} {
    margin-right: 30px;
    margin-left: 30px;
  }
`;

const AllRightsReserved = styled(Copyright)`
  color: white;
  margin-top: 20px;
  font-size: 14px;
`;

const InfoLinkSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 10px;
  align-items: center;

  @media ${BREAK_POINTS.tablet} {
    margin-top: inherit;
  }
`;

const Separator = styled.div`
  margin-right: 10px;
  margin-left: 10px;
  color: white;
  font-size: 12px;
`;

const InfoLinkContainer = styled.div`
  display: flex;
`;

const menuItems: NavItem[] = getPartnerAsset(DynamicAssetsKeys.footerNavLinks);
const loggedInMenuItems: NavItem[] = getPartnerAsset(DynamicAssetsKeys.loggedInFooterNavLinks);

const partnerLogoHref: string = getPartnerAsset(DynamicAssetsKeys.footerPartnerLogoHref);
const partnerLogo: string = getPartnerAsset(DynamicAssetsKeys.footerPartnerLogoImage);

const isOnlineClaimEnabled: boolean = getPartnerAsset(DynamicAssetsKeys.footerOnlineClaimButtonEnabled);

const BrandedFooter: React.FunctionComponent<FooterProps> = (props) => {
  const user = useUser();
  const [partner] = usePartner();
  const claimUrl = partnerValue({ TechCoachHome: user ? partner.claimUrl : '/dashboard' }, partner.claimUrl);
  const claimsPhoneNumber = partner.helpNumber;
  const claimsHref = phoneToUrl(claimsPhoneNumber);

  const items = prepareNavItems(props.isAuthEnabled && user ? loggedInMenuItems : menuItems, {
    vars: { claimUrl: claimUrl || '' },
    isAuthEnabled: props.isAuthEnabled,
  });

  return (
    <Background>
      <Container>
        <Section side={Sides.Top}>
          <SectionGroup side={Sides.Left}>
            <FileClaimText>Need help? Call us.</FileClaimText>
            <ClaimPhoneText>{claimsPhoneNumber}</ClaimPhoneText>
            <CallButton href={claimsHref}>{claimsPhoneNumber}</CallButton>
            {isOnlineClaimEnabled && (
              <OnlineClaimButton href={claimUrl} target={user ? '_blank' : ''}>
                File a claim online
              </OnlineClaimButton>
            )}
          </SectionGroup>

          <SectionGroup side={Sides.Right}>
            {items.map((link, i) => (
              <Link key={i} {...link}>
                {link.label}
              </Link>
            ))}
          </SectionGroup>
        </Section>

        <Section side={Sides.Bottom}>
          <SectionGroup side={Sides.Left}>
            <LogosContainer>
              <LogoLink href="https://www.asurion.com/">
                <AsurionLogo src={asurionLogo} />
              </LogoLink>
              {partnerLogo ? (
                <>
                  <LogoSeparator />
                  <LogoLink href={partnerLogoHref}>
                    <PartnerLogo src={partnerLogo} />
                  </LogoLink>
                </>
              ) : null}
            </LogosContainer>
            <AllRightsReserved />
          </SectionGroup>

          <SectionGroup side={Sides.Right}>
            <InfoLinkSection>
              {legalNavItems.map((link, i) =>
                i === 0 ? (
                  <InformativeLink key={i} {...link}>
                    {link.label}
                  </InformativeLink>
                ) : (
                  <InfoLinkContainer key={i}>
                    <Separator>|</Separator>
                    <InformativeLink {...link}>{link.label}</InformativeLink>
                  </InfoLinkContainer>
                )
              )}
            </InfoLinkSection>
          </SectionGroup>
        </Section>
      </Container>
    </Background>
  );
};

export default BrandedFooter;



// WEBPACK FOOTER //
// ./packages/smart-home-support/components/Footer/types/BrandedFooter.tsx